import type { Quest, QuestObjective, QuestStatus, Reward } from '../../types/features';
import { playerService } from '../player/playerService';

class QuestService {
    private static instance: QuestService;
    private quests: Quest[] = [
        {
            id: 'daily-spins',
            name: 'Daily Spins Challenge',
            description: 'Complete 100 spins today to earn bonus coins!',
            startDate: '2024-03-20',
            endDate: '2024-03-21',
            status: 'active',
            objectives: [
                {
                    id: 'spins-100',
                    description: 'Spin 100 times',
                    conditionType: 'spin',
                    threshold: 100,
                    currentProgress: 45
                }
            ],
            reward: {
                type: 'game_coins',
                amount: 1000
            },
            bannerUrl: 'https://images.unsplash.com/photo-1518544801976-3e159e50e5bb?w=800&h=400&fit=crop',
            isLinear: false
        },
        {
            id: 'big-win-quest',
            name: 'Big Win Quest',
            description: 'Win big to earn massive XP boost!',
            startDate: '2024-03-20',
            endDate: '2024-03-27',
            status: 'active',
            objectives: [
                {
                    id: 'win-1000',
                    description: 'Win 1000 coins in a single spin',
                    conditionType: 'win',
                    threshold: 1000,
                    currentProgress: 750
                }
            ],
            reward: {
                type: 'exp',
                amount: 2000
            },
            bannerUrl: 'https://images.unsplash.com/photo-1533228876829-65c94e7b5025?w=800&h=400&fit=crop',
            isLinear: false
        },
        {
            id: 'high-roller',
            name: 'High Roller Challenge',
            description: 'Place high-value bets to earn exclusive rewards!',
            startDate: '2024-03-20',
            endDate: '2024-03-23',
            status: 'active',
            objectives: [
                {
                    id: 'bet-5000',
                    description: 'Bet a total of 5000 coins',
                    conditionType: 'bet',
                    threshold: 5000,
                    currentProgress: 2300
                }
            ],
            reward: {
                type: 'free_spins',
                amount: 50
            },
            bannerUrl: 'https://images.unsplash.com/photo-1518791841217-8f162f1e1131?w=800&h=400&fit=crop',
            isLinear: false
        },
        {
            id: 'lucky-streak',
            name: 'Lucky Streak',
            description: 'Complete this quest chain to earn massive rewards!',
            startDate: '2024-03-20',
            endDate: '2024-03-25',
            status: 'active',
            objectives: [
                {
                    id: 'win-streak',
                    description: 'Win 5 times in a row',
                    conditionType: 'win',
                    threshold: 5,
                    currentProgress: 3
                }
            ],
            reward: {
                type: 'money',
                amount: 100
            },
            bannerUrl: 'https://images.unsplash.com/photo-1513151233558-d860c5398176?w=800&h=400&fit=crop',
            isLinear: true,
            questGroupId: 'lucky-chain'
        },
        {
            id: 'diamond-collector',
            name: 'Diamond Collector',
            description: 'Collect diamond symbols to earn special rewards!',
            startDate: '2024-03-20',
            endDate: '2024-03-22',
            status: 'active',
            objectives: [
                {
                    id: 'collect-diamonds',
                    description: 'Collect 10 diamond symbols',
                    conditionType: 'collect',
                    threshold: 10,
                    currentProgress: 6
                }
            ],
            reward: {
                type: 'game_coins',
                amount: 5000
            },
            bannerUrl: 'https://images.unsplash.com/photo-1515541324332-7dd0c37426e0?w=800&h=400&fit=crop',
            isLinear: false
        }
    ];

    private constructor() { }

    static getInstance(): QuestService {
        if (!QuestService.instance) {
            QuestService.instance = new QuestService();
        }
        return QuestService.instance;
    }

    getQuests(): Quest[] {
        return [...this.quests];
    }

    getActiveQuests(): Quest[] {
        return this.quests.filter(quest => quest.status === 'active');
    }

    getQuestById(questId: string): Quest | undefined {
        return this.quests.find(quest => quest.id === questId);
    }

    updateQuestProgress(questId: string, eventType: string, value: number): void {
        const quest = this.quests.find(q => q.id === questId);
        if (!quest || quest.status !== 'active') return;

        quest.objectives.forEach(objective => {
            if (objective.conditionType === eventType) {
                if (eventType === 'win') {
                    objective.currentProgress = Math.max(objective.currentProgress, value);
                } else {
                    objective.currentProgress += value;
                }

                if (this.checkQuestCompletion(quest)) {
                    this.completeQuest(quest);
                }
            }
        });
    }

    private checkQuestCompletion(quest: Quest): boolean {
        return quest.objectives.every(
            objective => objective.currentProgress >= objective.threshold
        );
    }

    private completeQuest(quest: Quest): void {
        quest.status = 'completed';
        this.applyReward(quest.reward);
    }

    private applyReward(reward: Reward): void {
        switch (reward.type) {
            case 'game_coins':
                playerService.addGameCoins(reward.amount);
                break;
            case 'exp':
                playerService.addXP(reward.amount);
                break;
            case 'free_spins':
                // Handle free spins
                break;
            case 'money':
                // Handle money reward
                break;
        }
    }
}

export const questService = QuestService.getInstance(); 